// Supermarket Sweep decor (Level 8): the player runs down a supermarket
// aisle with shelves on both sides. Hazards include falling products,
// breakable cracked-floor tiles, slippery spill patches and holes in the
// floor. Word gates sit on safe pads between hazard sections.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";
import { sfx } from "../audio";

const TRACK_TOP_Y = 0.18;
const TILE_THICKNESS = 0.32;
const FLOOR_GREY = 0xeaeefb;
const FLOOR_GREY_ALT = 0xc6cce3;
const CRACK_COLOR = 0xb1b6c8;
const SHELF_BACK = 0xeae0d3;
const SHELF_TRIM = 0x6c43c5;
const SHELF_ACCENT = 0xff5fa7;

// Product palette — bright candy-bright colours so the aisle is busy
// without us baking textures.
const PRODUCT_COLORS = [
  0xff5fa7, 0x6bd0ff, 0xffd166, 0x6be09b, 0xff8a3d,
  0xa855f7, 0xfde047, 0xef4444, 0x14b8a6, 0xffe066,
];

type CrackedState = "idle" | "cracked" | "falling" | "broken";

interface CrackedTile {
  s: number;
  l: number;
  halfDepth: number;
  halfWidth: number;
  state: CrackedState;
  crackedAt: number | null;
  fallingAt: number | null;
  mesh: THREE.Mesh;
}

interface FallingProduct {
  /** Anchor on the shelf (s, side). */
  s: number;
  side: 1 | -1;
  /** Period in ms — one drop cycle (fall + reset + idle). */
  periodMs: number;
  /** Phase offset 0..1. */
  phase: number;
  group: THREE.Group;
  /** Half-extent of the product box for player collision. */
  radius: number;
  /** Latest computed world position (cached). */
  worldX: number;
  worldY: number;
}

export interface SupermarketSweepHandle {
  group: THREE.Group;
  supportMeshes: THREE.Object3D[];
  gatePositions: number[];
  tick: (clockMs: number, steppers: { id: string; s: number; l: number }[]) => void;
  /** True if (s, l) is on solid (not-broken) floor — used by Game's fall
   *  check to bail out into a fall when the player isn't supported. */
  hasFloorSupport: (s: number, l: number) => boolean;
  /** Returns the slip vector (dx, dy) per second that should be applied
   *  to the player while standing in a spill zone. Zero outside any. */
  slipPushAt: (s: number, l: number) => { dx: number; dy: number };
  /** Contact check for falling-product hits — returns the impulse to
   *  apply or null. Same shape as the other bar-contact handlers. */
  productContact: (s: number, l: number, worldY: number, radius: number) => {
    normalL: number;
    normalS: number;
    penetration: number;
    barVelL: number;
    barVelS: number;
  } | null;
}

function buildFloorSegment(width: number, depth: number, color: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new SoftBox(width, TILE_THICKNESS, depth),
    new THREE.MeshStandardMaterial({ color, roughness: 0.62 }),
  );
  mesh.receiveShadow = true;
  return mesh;
}

function buildShelfWall(length: number, sideSign: 1 | -1, halfWidth: number): THREE.Group {
  const g = new THREE.Group();
  const SHELF_HEIGHT = 3.4;
  const SHELF_DEPTH = 1.6;
  // Back panel (tall slab) flush against the aisle.
  const back = new THREE.Mesh(
    new SoftBox(SHELF_DEPTH, SHELF_HEIGHT, length),
    new THREE.MeshStandardMaterial({ color: SHELF_BACK, roughness: 0.5 }),
  );
  back.position.set(sideSign * (halfWidth + SHELF_DEPTH / 2 - 0.05), SHELF_HEIGHT / 2 + TRACK_TOP_Y, length / 2);
  back.castShadow = true;
  back.receiveShadow = true;
  g.add(back);
  // Trim along the top to make the shelf look "finished" rather than a slab.
  const trim = new THREE.Mesh(
    new SoftBox(SHELF_DEPTH * 1.05, 0.34, length),
    new THREE.MeshStandardMaterial({ color: SHELF_TRIM, roughness: 0.45 }),
  );
  trim.position.set(sideSign * (halfWidth + SHELF_DEPTH / 2 - 0.05), SHELF_HEIGHT + 0.05 + TRACK_TOP_Y, length / 2);
  g.add(trim);
  // Skirting board at the floor.
  const skirt = new THREE.Mesh(
    new SoftBox(SHELF_DEPTH * 0.85, 0.4, length),
    new THREE.MeshStandardMaterial({ color: SHELF_ACCENT, roughness: 0.4 }),
  );
  skirt.position.set(sideSign * (halfWidth + SHELF_DEPTH / 2 - 0.05), 0.2 + TRACK_TOP_Y, length / 2);
  g.add(skirt);
  // Horizontal shelf bands at 3 heights.
  const bandMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 });
  for (const bandY of [0.85, 1.7, 2.55]) {
    const band = new THREE.Mesh(
      new SoftBox(SHELF_DEPTH * 0.85, 0.1, length),
      bandMat,
    );
    band.position.set(sideSign * (halfWidth + SHELF_DEPTH / 2 - 0.05), bandY + TRACK_TOP_Y, length / 2);
    g.add(band);
  }
  return g;
}

/** Instanced product props on a shelf wall. Filled along the length with
 *  random colours + simple box/cylinder shapes. One InstancedMesh per
 *  shape keeps draw calls bounded. */
function fillShelfProducts(group: THREE.Group, length: number, sideSign: 1 | -1, halfWidth: number) {
  // Use a single shared geometry for boxes (cereal/cracker boxes) and
  // another for cans (cylinders). Per-instance colour via instanceColor.
  const BAND_YS = [0.85, 1.7, 2.55];
  const PRODUCTS_PER_BAND = Math.max(6, Math.floor(length / 1.6));

  // Box-style products.
  const boxGeo = new THREE.BoxGeometry(0.55, 0.7, 0.45);
  const boxMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5 });
  const boxInst = new THREE.InstancedMesh(boxGeo, boxMat, BAND_YS.length * PRODUCTS_PER_BAND);
  const colors = new Float32Array(boxInst.count * 3);
  const tmp = new THREE.Object3D();
  let idx = 0;
  for (let b = 0; b < BAND_YS.length; b++) {
    const bandY = BAND_YS[b]!;
    for (let i = 0; i < PRODUCTS_PER_BAND; i++) {
      const z = (i + 0.5) * (length / PRODUCTS_PER_BAND);
      // Push slightly into the shelf wall so the front face peeks into
      // the aisle.
      const x = sideSign * (halfWidth + 0.35);
      tmp.position.set(x, bandY + TRACK_TOP_Y + 0.05, z);
      tmp.rotation.set(0, sideSign === 1 ? Math.PI : 0, 0);
      tmp.scale.set(0.8 + Math.random() * 0.5, 0.7 + Math.random() * 0.6, 1);
      tmp.updateMatrix();
      boxInst.setMatrixAt(idx, tmp.matrix);
      const c = new THREE.Color(PRODUCT_COLORS[(b * 7 + i * 3) % PRODUCT_COLORS.length]!);
      colors[idx * 3 + 0] = c.r;
      colors[idx * 3 + 1] = c.g;
      colors[idx * 3 + 2] = c.b;
      idx++;
    }
  }
  boxInst.instanceColor = new THREE.InstancedBufferAttribute(colors, 3);
  boxInst.instanceMatrix.needsUpdate = true;
  group.add(boxInst);
}

/** Glossy spill patch — a flat plane laid just above the floor with arrow
 *  decals showing slide direction. */
function buildSpillPatch(width: number, depth: number, dir: 1 | -1): THREE.Group {
  const g = new THREE.Group();
  const baseMat = new THREE.MeshStandardMaterial({
    color: 0xffe066,
    emissive: 0xffd166,
    emissiveIntensity: 0.25,
    metalness: 0.4,
    roughness: 0.18,
    transparent: true,
    opacity: 0.92,
  });
  const base = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), baseMat);
  base.rotation.x = -Math.PI / 2;
  base.position.y = TRACK_TOP_Y + 0.03;
  g.add(base);
  // Streak arrows pointing in slide direction (lateral X).
  const arrowMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.65 });
  const arrowCount = Math.max(3, Math.floor(depth / 1.2));
  for (let i = 0; i < arrowCount; i++) {
    const arr = new THREE.Mesh(new THREE.PlaneGeometry(width * 0.35, 0.35), arrowMat);
    arr.rotation.x = -Math.PI / 2;
    arr.rotation.z = dir === 1 ? -Math.PI / 2 : Math.PI / 2;
    arr.position.set(0, TRACK_TOP_Y + 0.04, -depth / 2 + (i + 0.5) * (depth / arrowCount));
    g.add(arr);
  }
  return g;
}

/** Build a procedural cracked-tile mesh — a flat block with darker
 *  jagged streaks "painted" on the top via a small canvas texture. */
let _crackTex: THREE.CanvasTexture | null = null;
function crackedTileTexture(): THREE.CanvasTexture {
  if (_crackTex) return _crackTex;
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = `#${FLOOR_GREY.toString(16).padStart(6, "0")}`;
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = `#${CRACK_COLOR.toString(16).padStart(6, "0")}`;
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  const cx = size / 2;
  const cy = size / 2;
  for (let i = 0; i < 5; i++) {
    const ang = (i / 5) * Math.PI * 2 + Math.random() * 0.4;
    let x = cx;
    let y = cy;
    ctx.beginPath();
    ctx.moveTo(x, y);
    for (let j = 0; j < 3; j++) {
      x += Math.cos(ang) * (18 + Math.random() * 10);
      y += Math.sin(ang) * (18 + Math.random() * 10);
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  _crackTex = new THREE.CanvasTexture(canvas);
  _crackTex.colorSpace = THREE.SRGBColorSpace;
  return _crackTex;
}

export function buildSupermarketSweepCourse(path: Path): SupermarketSweepHandle {
  const ss = path.supermarketSweep;
  if (!ss) throw new Error("buildSupermarketSweepCourse called on non-supermarket-sweep path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const trackWidth = ss.halfWidth * 2;
  const tiles: CrackedTile[] = [];
  const products: FallingProduct[] = [];

  // ── Solid floor: paved with regular tiles in every s-range that isn't
  // a cracked zone or hole. We split the course at zone boundaries and
  // build a flat panel for each gap.
  const cuts: Array<{ s: number; kind: "open" | "cracked-start" | "cracked-end" | "hole-start" | "hole-end" }> = [
    { s: 0, kind: "open" },
    { s: ss.length, kind: "open" },
  ];
  for (const z of ss.crackedZones) {
    cuts.push({ s: z.start, kind: "cracked-start" });
    cuts.push({ s: z.end, kind: "cracked-end" });
  }
  for (const h of ss.holes) {
    cuts.push({ s: h.start, kind: "hole-start" });
    cuts.push({ s: h.end, kind: "hole-end" });
  }
  cuts.sort((a, b) => a.s - b.s);

  // Walk segment-by-segment.
  for (let i = 0; i < cuts.length - 1; i++) {
    const a = cuts[i]!;
    const b = cuts[i + 1]!;
    const len = b.s - a.s;
    if (len < 0.5) continue;
    const inCracked = ss.crackedZones.some((z) => a.s >= z.start && b.s <= z.end);
    const inHole = ss.holes.some((h) => a.s >= h.start && b.s <= h.end);
    if (inCracked || inHole) continue;
    // Two-tone tile pattern: alternate light/dark colour per row.
    const tileColor = (Math.round(a.s / 2) % 2 === 0) ? FLOOR_GREY : FLOOR_GREY_ALT;
    const slab = buildFloorSegment(trackWidth, len, tileColor);
    slab.position.set(0, 0, a.s + len / 2);
    group.add(slab);
    supportMeshes.push(slab);
  }

  // ── Cracked-tile patches.
  // Subdivide each cracked-zone into a 2D grid of tiles. Each tile is a
  // separate mesh + support, so when it breaks it can be removed without
  // disturbing neighbours.
  const tileColMat = new THREE.MeshStandardMaterial({
    map: crackedTileTexture(),
    roughness: 0.65,
  });
  for (const zone of ss.crackedZones) {
    const tileDepth = 1.4;
    const tileWidthApprox = 1.4;
    const rows = Math.max(2, Math.round((zone.end - zone.start) / tileDepth));
    const cols = Math.max(3, Math.round(trackWidth / tileWidthApprox));
    const dRow = (zone.end - zone.start) / rows;
    const dCol = trackWidth / cols;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const sCenter = zone.start + dRow * (r + 0.5);
        const lCenter = -ss.halfWidth + dCol * (c + 0.5);
        const mesh = new THREE.Mesh(
          new SoftBox(dCol * 0.92, TILE_THICKNESS, dRow * 0.92),
          tileColMat,
        );
        mesh.position.set(lCenter, 0, sCenter);
        mesh.receiveShadow = true;
        group.add(mesh);
        supportMeshes.push(mesh);
        tiles.push({
          s: sCenter, l: lCenter,
          halfDepth: dRow / 2,
          halfWidth: dCol / 2,
          state: "idle",
          crackedAt: null,
          fallingAt: null,
          mesh,
        });
      }
    }
  }

  // ── Spill patches.
  for (const z of ss.slipperyZones) {
    const patch = buildSpillPatch(trackWidth * 0.78, z.end - z.start, z.dir);
    patch.position.set(0, 0, (z.start + z.end) / 2);
    group.add(patch);
  }

  // ── Holes — render warning-crack rings around the edges of each hole.
  const ringMat = new THREE.MeshStandardMaterial({ color: 0x4a4a5a, roughness: 0.6 });
  for (const h of ss.holes) {
    const hLen = h.end - h.start;
    const hWidth = h.lateralTo - h.lateralFrom;
    const cx = (h.lateralFrom + h.lateralTo) / 2;
    // Dark "void" cap below so the hole shows real depth.
    const voidMat = new THREE.MeshBasicMaterial({ color: 0x0d0d18 });
    const voidPad = new THREE.Mesh(
      new THREE.PlaneGeometry(hWidth, hLen),
      voidMat,
    );
    voidPad.rotation.x = -Math.PI / 2;
    voidPad.position.set(cx, -4, (h.start + h.end) / 2);
    group.add(voidPad);
    // Cracked rim around the hole — four thin slabs.
    const RIM = 0.32;
    const top = new THREE.Mesh(new SoftBox(hWidth + RIM * 2, 0.12, RIM), ringMat);
    top.position.set(cx, TRACK_TOP_Y + 0.05, h.end);
    group.add(top);
    const bot = top.clone();
    bot.position.z = h.start;
    group.add(bot);
    const left = new THREE.Mesh(new SoftBox(RIM, 0.12, hLen), ringMat);
    left.position.set(h.lateralFrom, TRACK_TOP_Y + 0.05, (h.start + h.end) / 2);
    group.add(left);
    const right = left.clone();
    right.position.x = h.lateralTo;
    group.add(right);
  }

  // ── Shelves on both sides, full length.
  const shelfL = buildShelfWall(ss.length, -1, ss.halfWidth);
  group.add(shelfL);
  fillShelfProducts(shelfL, ss.length, -1, ss.halfWidth);
  const shelfR = buildShelfWall(ss.length, 1, ss.halfWidth);
  group.add(shelfR);
  fillShelfProducts(shelfR, ss.length, 1, ss.halfWidth);

  // ── Falling-product cannons. Each fires a coloured cube into the
  // aisle on a deterministic cycle so multiplayer stays in lockstep.
  for (let i = 0; i < ss.productCannons.length; i++) {
    const cn = ss.productCannons[i]!;
    const productGroup = new THREE.Group();
    const radius = 0.42;
    const mesh = new THREE.Mesh(
      new SoftBox(radius * 2, radius * 2, radius * 2),
      new THREE.MeshStandardMaterial({
        color: PRODUCT_COLORS[i % PRODUCT_COLORS.length]!,
        emissive: 0x000000,
        roughness: 0.5,
      }),
    );
    mesh.castShadow = true;
    productGroup.add(mesh);
    productGroup.position.set(cn.side * (ss.halfWidth + 0.4), 2.2, cn.s);
    group.add(productGroup);
    products.push({
      s: cn.s,
      side: cn.side,
      periodMs: 3500 + (i % 3) * 500,
      phase: (i * 0.27) % 1,
      group: productGroup,
      radius,
      worldX: cn.side * (ss.halfWidth + 0.4),
      worldY: 2.2,
    });
  }

  // ── Finish line: checkered strip + big sign at the very end.
  const finishStripe = new THREE.Mesh(
    new THREE.PlaneGeometry(trackWidth, 2.4),
    new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6 }),
  );
  finishStripe.rotation.x = -Math.PI / 2;
  finishStripe.position.set(0, TRACK_TOP_Y + 0.04, ss.length - 8);
  group.add(finishStripe);
  for (let i = 0; i < 8; i++) {
    const sq = new THREE.Mesh(
      new THREE.PlaneGeometry(trackWidth / 8, 1.2),
      new THREE.MeshStandardMaterial({
        color: i % 2 === 0 ? 0x0a0a0a : 0xfafafa,
        roughness: 0.7,
      }),
    );
    sq.rotation.x = -Math.PI / 2;
    sq.position.set(-trackWidth / 2 + (i + 0.5) * (trackWidth / 8), TRACK_TOP_Y + 0.05, ss.length - 8);
    group.add(sq);
  }
  // CHECKOUT FINISH arch.
  const archMat = new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.5 });
  for (const x of [-ss.halfWidth - 0.5, ss.halfWidth + 0.5]) {
    const post = new THREE.Mesh(new SoftBox(0.4, 4.4, 0.4), archMat);
    post.position.set(x, 2.2 + TRACK_TOP_Y, ss.length - 4);
    group.add(post);
  }
  const banner = new THREE.Mesh(
    new SoftBox(trackWidth + 1.4, 0.8, 0.4),
    new THREE.MeshStandardMaterial({
      color: 0xffd166,
      emissive: 0xff8a3d,
      emissiveIntensity: 0.4,
    }),
  );
  banner.position.set(0, 4.6 + TRACK_TOP_Y, ss.length - 4);
  group.add(banner);

  // ── Helpers / mechanics ──

  const LINGER_MS = 700;
  const FALL_MS = 900;

  const overlapsTile = (t: CrackedTile, s: number, l: number) =>
    Math.abs(s - t.s) < t.halfDepth + 0.4 && Math.abs(l - t.l) < t.halfWidth + 0.4;

  const tick = (clockMs: number, steppers: { id: string; s: number; l: number }[]) => {
    // Cracked tile state machine (mirrors ice-crack pattern, simpler).
    for (const t of tiles) {
      if (t.state === "broken") continue;
      let onIt = false;
      for (const sp of steppers) {
        if (overlapsTile(t, sp.s, sp.l)) { onIt = true; break; }
      }
      if (t.state === "idle" && onIt) {
        t.state = "cracked";
        t.crackedAt = clockMs;
        (t.mesh.material as THREE.MeshStandardMaterial).color.setHex(CRACK_COLOR);
        sfx.ice(0.6);
      } else if (t.state === "cracked" && t.crackedAt != null) {
        if (clockMs - t.crackedAt >= LINGER_MS && onIt) {
          t.state = "falling";
          t.fallingAt = clockMs;
          t.mesh.visible = false;
          sfx.ice(0.85);
        }
      } else if (t.state === "falling" && t.fallingAt != null) {
        if (clockMs - t.fallingAt >= FALL_MS) {
          t.state = "broken";
          t.mesh.position.y = -10;
        }
      }
    }

    // Deterministic product fall cycle. Each product spends ~40% of
    // its period falling, ~10% sitting on the floor, ~50% being
    // reset back to the shelf.
    for (const p of products) {
      const t = ((clockMs / p.periodMs) + p.phase) % 1;
      let y = 2.2;
      let x = p.side * (path.supermarketSweep!.halfWidth + 0.4);
      if (t < 0.4) {
        // Falling: x sweeps from shelf edge inward; y drops.
        const u = t / 0.4;
        y = 2.2 - u * (2.2 - TRACK_TOP_Y);
        x = p.side * ((path.supermarketSweep!.halfWidth + 0.4) - u * (path.supermarketSweep!.halfWidth + 0.4));
      } else if (t < 0.5) {
        // On floor briefly.
        y = TRACK_TOP_Y;
        x = 0;
      } else {
        // Reset: lift back into shelf with no spin.
        const u = (t - 0.5) / 0.5;
        y = TRACK_TOP_Y + u * (2.2 - TRACK_TOP_Y);
        x = p.side * (u * (path.supermarketSweep!.halfWidth + 0.4));
      }
      p.group.position.set(x, y, p.s);
      p.group.rotation.x = t * Math.PI * 4;
      p.group.rotation.z = t * Math.PI * 2 * (p.side === 1 ? -1 : 1);
      p.worldX = x;
      p.worldY = y;
    }
  };

  const hasFloorSupport = (s: number, l: number): boolean => {
    // Holes: explicit no-support range.
    for (const h of ss.holes) {
      if (s < h.start || s > h.end) continue;
      if (l >= h.lateralFrom && l <= h.lateralTo) return false;
    }
    // Cracked tiles: only supported if at least one tile under the
    // player isn't broken.
    const inCracked = ss.crackedZones.some((z) => s >= z.start && s <= z.end);
    if (inCracked) {
      for (const t of tiles) {
        if (t.state === "broken") continue;
        if (Math.abs(s - t.s) <= t.halfDepth + 0.1 && Math.abs(l - t.l) <= t.halfWidth + 0.1) {
          return true;
        }
      }
      return false;
    }
    // Default: solid floor everywhere else.
    return true;
  };

  const slipPushAt = (s: number, l: number): { dx: number; dy: number } => {
    for (const z of ss.slipperyZones) {
      if (s < z.start || s > z.end) continue;
      if (Math.abs(l) > ss.halfWidth) continue;
      return { dx: z.dir * 6.0, dy: 0 };
    }
    return { dx: 0, dy: 0 };
  };

  const productContact = (s: number, l: number, worldY: number, radius: number) => {
    for (const p of products) {
      if (Math.abs(s - p.s) > radius + p.radius) continue;
      // World X of the product head is p.worldX. Check 3D distance.
      const dx = l - p.worldX;
      const dy = worldY - p.worldY;
      const dz = s - p.s;
      const dist2 = dx * dx + dy * dy + dz * dz;
      const sumR = radius + p.radius;
      if (dist2 >= sumR * sumR) continue;
      const dist = Math.sqrt(Math.max(0.0001, dist2));
      const nl = dx / dist;
      const ns = dz / dist;
      return {
        normalL: nl,
        normalS: ns,
        penetration: sumR - dist,
        // Product is falling/sliding — approximate vel magnitude.
        barVelL: -nl * 5,
        barVelS: -ns * 2,
      };
    }
    return null;
  };

  return {
    group,
    supportMeshes,
    gatePositions: ss.gates,
    tick,
    hasFloorSupport,
    slipPushAt,
    productContact,
  };
}
